import { FC, MouseEventHandler, ReactNode, useCallback, useRef } from 'react';
import AliceCarousel, { Props as AliceCarouselProps } from 'react-alice-carousel';
import { Box } from '@bridebook/ui';
import { IconChevronRight } from '@bridebook/ui/src/icons/dynamic';
import IconRoundButton, {
  IProps as IconRoundButtonProps,
} from '../icon-round-button/icon-round-button';
import { componentStyles } from './slider.style';
import { useBlockVerticalScrollOnHorizontalSwipe } from './use-block-vertical-scroll-on-horizontal-swipe';

interface IProps {
  items: ReactNode[];
  showControlsOnHover?: boolean;
  sliderProps?: AliceCarouselProps;
  onClickPrev?: MouseEventHandler<ReactNode>;
  onClickNext?: MouseEventHandler<ReactNode>;
  blockScrollOnSwipe?: boolean;
}

const defaultAliceCarouselProps: AliceCarouselProps = {
  mouseTracking: false,
  infinite: true,
};

const PrevButton = ({ onClick }: { onClick: IconRoundButtonProps['onClick'] }) => (
  <IconRoundButton
    border={false}
    onClick={onClick}
    backgroundColor="space"
    icon={
      <IconChevronRight style={{ transform: 'rotate(180deg) translateX(1px)' }} color="white" />
    }
    size={30}
  />
);
const NextButton = ({ onClick }: { onClick: IconRoundButtonProps['onClick'] }) => (
  <IconRoundButton
    border={false}
    onClick={onClick}
    backgroundColor="space"
    icon={<IconChevronRight style={{ transform: 'translateX(1px)' }} color="white" />}
    size={30}
  />
);

const handleWrapperEventPropagation: MouseEventHandler<ReactNode> = (e) => {
  if (e.target instanceof HTMLElement && e.target.classList.contains('alice-carousel__dots-item')) {
    e.stopPropagation();
  }
};

export const Slider: FC<IProps> = ({
  items,
  showControlsOnHover = true,
  sliderProps = {},
  blockScrollOnSwipe = false,
}) => {
  const styles = componentStyles({ showControlsOnHover });
  const sliderRef = useRef<AliceCarousel>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useBlockVerticalScrollOnHorizontalSwipe(wrapperRef, blockScrollOnSwipe);

  const onClickPrev: MouseEventHandler<ReactNode> = useCallback((e) => {
    e.stopPropagation();
    sliderRef.current.slidePrev();
  }, []);

  const onClickNext: MouseEventHandler<ReactNode> = useCallback((e) => {
    e.stopPropagation();
    sliderRef.current.slideNext();
  }, []);

  return (
    <Box setRef={wrapperRef} style={styles.wrapper} onClick={handleWrapperEventPropagation}>
      <AliceCarousel
        items={items}
        {...defaultAliceCarouselProps}
        {...sliderProps}
        ref={sliderRef}
        renderPrevButton={({ isDisabled }) =>
          !isDisabled &&
          !sliderProps?.disableButtonsControls && <PrevButton onClick={onClickPrev} />
        }
        renderNextButton={({ isDisabled }) =>
          !isDisabled &&
          !sliderProps?.disableButtonsControls && <NextButton onClick={onClickNext} />
        }
      />
    </Box>
  );
};
